/**
 * TanStack Query hooks for the BI domain (dashboards / widgets / queries).
 *
 * BI is plain REST on the V2 API and NOT offline-first, so it belongs on
 * TanStack Query (see `query/CLAUDE.md` boundary rule), reading through the
 * typed `biApi` client. Read hooks for all three resources, full dashboard
 * mutations (the user owns dashboard lifecycle), and the one widget action
 * `refresh`. Query writes are omitted — queries are AI-authored.
 *
 * This is the client/hooks layer only (STK-60). Nothing consumes these yet —
 * the BI page still renders from its mock store; swapping it over is a
 * follow-up ticket (STK-148 / STK-155).
 */
import { useEffect, useRef } from "react";
import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from "@tanstack/react-query";

import { useUnreadBiDashboardIds } from "@stockline/web";

import { biApi, WidgetRefreshError } from "@/lib/v2-api/bi-client";
import type {
  Dashboard,
  DashboardCreateInput,
  DashboardListItem,
  DashboardPayload,
  DashboardUpdateInput,
  DashboardWidgetChanges,
  DashboardWidgetView,
  LayoutPlacementInput,
  QueryExecutionPayload,
  ResolvedWidget,
  Widget,
} from "@/lib/v2-api/bi-types";
import { biKeys } from "../keys/bi";

// ---------------------------------------------------------------- reads ----

/**
 * Order the dashboard list by the user-chosen `position` (dense 0..n-1), so the
 * tab strip renders in the order the user arranged. Ties break on
 * `createdAt` (oldest first) so a transient duplicate position — e.g. mid
 * optimistic reorder — still sorts deterministically. The backend already
 * orders by position; sorting here keeps every consumer (tab strip + index
 * redirect) on one order regardless of fetch source, and a STABLE sort makes it
 * idempotent. Module-scoped so TanStack memoizes the transform (stable `select`
 * reference → no per-render array churn).
 */
function compareByPosition(a: Dashboard, b: Dashboard): number {
  if (a.position !== b.position) return a.position - b.position;
  if (a.createdAt < b.createdAt) return -1;
  if (a.createdAt > b.createdAt) return 1;
  return 0;
}

const selectDashboardsByPosition = (
  rows: DashboardListItem[],
): DashboardListItem[] => rows.slice().sort(compareByPosition);

/** All dashboards owned by the current user (metadata only), in tab order. */
export function useDashboards() {
  return useQuery({
    queryKey: biKeys.dashboardList(),
    queryFn: () => biApi.dashboards.list(),
    select: selectDashboardsByPosition,
    // Poll the tab list so an AI-created dashboard shows up without a manual
    // refresh (BI isn't offline-first → no SSE push). Cheap (metadata only);
    // paused while the browser tab is backgrounded.
    refetchInterval: 10000,
    refetchIntervalInBackground: false,
  });
}

/**
 * Number of dashboards whose tab is "unread" — i.e. carries widgets its owner
 * has never had on screen (`hasUnseenWidgets`) OR an unread BI brief attributed
 * to it. Drives the steering nav badge (STK-1658), which mirrors the tab strip
 * one-for-one: each lit tab counts once, no matter how many briefs or new
 * widgets it holds. A brief and new widgets on the SAME dashboard is still one
 * tab, so the two signals are unioned per dashboard, never summed.
 *
 * Counting over the real dashboard list (not the raw id sets) self-heals stale
 * attributions: an unread brief pointing at a deleted dashboard, or an
 * unattributed one, lights no tab and so is not counted — exactly what the tab
 * strip shows. Rides the same polled tab list as `useDashboards`, so it adds no
 * request when steering is open and only a cheap metadata poll otherwise.
 */
export function useUnreadSteeringTabCount(): number {
  const { data } = useDashboards();
  const unreadDashboardIds = useUnreadBiDashboardIds();
  return (
    data?.reduce(
      (count, d) =>
        count + (d.hasUnseenWidgets || unreadDashboardIds.has(d.id) ? 1 : 0),
      0,
    ) ?? 0
  );
}

/** One dashboard composed with its resolved widgets (result + staleness). */
export function useDashboard(id: string | null | undefined) {
  return useQuery({
    queryKey: biKeys.dashboardDetail(id ?? ""),
    queryFn: () => biApi.dashboards.get(id as string),
    enabled: !!id,
  });
}

/**
 * Apply an incremental widget delta to a cached dashboard payload (STK-263).
 *
 * The poll transports three mutually-exclusive sets since the last cursor:
 * placements *created*, placements *updated* (definition or layout changed —
 * widget type/title/config/TTL, backing-query SQL, or layout), and the ids
 * *soft-deleted*. Applying it: drop the deleted placements (which also drops
 * their in-memory result data, since nothing else references it), replace each
 * updated placement in place (a NEW object identity, so its card re-renders
 * with the edit), append the freshly created ones, then sort by `position`.
 *
 * Every UNCHANGED surviving placement keeps its object identity, so its chart
 * never re-renders or re-animates. A bare result refresh is intentionally NOT
 * transported — that stays the explicit per-widget action `useRefreshWidget`,
 * which patches the cache in-session.
 *
 * Returns the SAME reference when the delta is a no-op so React short-circuits.
 * A no-op when there is no cached payload yet (the initial GET fills it first).
 */
export function applyDashboardDelta(
  prev: DashboardPayload | undefined,
  delta: DashboardWidgetChanges,
): DashboardPayload | undefined {
  if (!prev) return prev;
  if (
    delta.created.length === 0 &&
    delta.updated.length === 0 &&
    delta.deletedIds.length === 0
  ) {
    return prev;
  }

  const deleted = new Set(delta.deletedIds);
  const have = new Set(prev.widgets.map((w) => w.id));
  // Replace updated placements in place, keyed by id. An "updated" we don't
  // already hold is ignored (it would arrive via `created` instead, and keying
  // by id keeps a re-poll from ever duplicating one).
  const updatedById = new Map(
    delta.updated.filter((w) => have.has(w.id)).map((w) => [w.id, w]),
  );
  // Defensive: ignore any "created" we somehow already hold (cursor overlap).
  const fresh = delta.created.filter((w) => !have.has(w.id));

  let mutated = fresh.length > 0;
  const survivors: DashboardWidgetView[] = [];
  for (const w of prev.widgets) {
    if (deleted.has(w.id)) {
      mutated = true;
      continue;
    }
    const next = updatedById.get(w.id);
    if (next) {
      mutated = true;
      survivors.push(next); // new identity → the card re-renders with the edit
    } else {
      survivors.push(w); // untouched → keep identity, no chart re-animate
    }
  }

  // Second no-op path (the first is the all-empty short-circuit above): a delta
  // whose only entries are `updated`/`created` for ids we don't hold leaves
  // nothing to change — return the same reference so React short-circuits.
  if (!mutated) return prev;

  // Sort by position so layout/position edits — and the appended creates —
  // render in true order (`adaptDashboardPayload` preserves array order).
  const widgets = [...survivors, ...fresh].sort(
    (a, b) => a.position - b.position,
  );
  return { ...prev, widgets };
}

const DASHBOARD_POLL_INTERVAL_MS = 3000;
// Slower cadence when no chat is active: the user is just viewing, so we only
// need to catch the occasional out-of-band change (e.g. the AI editing this
// board from another chat/session), not keep pace with live authoring.
const DASHBOARD_IDLE_POLL_INTERVAL_MS = 10000;

/**
 * Poll the open dashboard for widget changes and patch them straight into the
 * `useDashboard` cache (STK-263) — so AI-authored widgets appear, AI-edited ones
 * update, and AI-deleted ones disappear without a manual refresh, and without
 * re-fetching the widgets already on screen. Runs whenever a dashboard is open
 * (BI isn't offline-first, so there's no SSE push); `active` (a chat is open)
 * only picks the cadence — fast while the AI may be authoring, slower when the
 * user is just viewing.
 *
 * The cursor bootstraps when the board first opens: the first call sends no
 * `since`, so the server just hands back its clock as the starting cursor (the
 * conversation has only just begun, so nothing has been authored to miss). Each
 * poll advances the cursor to the response's `serverTime`. The cursor is held in
 * a ref that survives cadence restarts (the `active` toggle) and resets only on
 * an `id` change, so flipping cadence resumes from the last cursor instead of
 * re-bootstrapping and dropping changes. A 404 (dashboard deleted / not the
 * caller's) stops the loop until inputs change.
 */
export function useDashboardLiveWidgets(
  id: string | null | undefined,
  active: boolean,
  // Bumped by the caller when an AI turn that may have authored a widget just
  // finished. Its value is meaningless — only a CHANGE matters: it re-runs the
  // effect, which fires `poll()` immediately, so a freshly-authored widget
  // lands on the board with the answer instead of up to a poll-interval later
  // (the final text is pushed live over SSE; the widget is not — BI has no
  // push, only this poll). Undefined/constant → the plain timer, unchanged.
  kick?: number,
) {
  const qc = useQueryClient();
  // The cursor lives OUTSIDE the effect so a cadence change (the `active` toggle
  // when a chat opens/closes) — which restarts the effect — resumes from the
  // last `serverTime` instead of re-bootstrapping to `null` and dropping any
  // widget changes that landed between the last poll and the restart. It resets
  // to bootstrap only when the dashboard `id` itself changes (a different board
  // has its own cursor).
  const sinceRef = useRef<string | null>(null);
  const lastIdRef = useRef<string | null | undefined>(undefined);
  if (lastIdRef.current !== id) {
    lastIdRef.current = id;
    sinceRef.current = null; // new board → bootstrap the cursor
  }
  useEffect(() => {
    if (!id) return;

    let cancelled = false;
    let handle: ReturnType<typeof setInterval> | undefined;
    let failureLogged = false; // warn once per failure streak (dev only)
    // Single-flight guard: a poll that runs longer than the interval would
    // otherwise let the next tick fire a second concurrent request, and the two
    // share `since` — the later-resolving one could write back an OLDER
    // `serverTime`, rolling the cursor backwards. Skipping a tick while one is
    // in flight keeps the cursor monotonic (self-healing anyway: the backend
    // over-fetches on `createdAt > cursor` and applyDashboardDelta de-dups, so
    // the only cost a race ever had was wasted bandwidth).
    let inflight = false;

    // Tear the loop down for good (no reschedule, no further ticks). Called both
    // on effect cleanup and from inside `poll` on a terminal 404.
    const stop = () => {
      cancelled = true;
      if (handle !== undefined) clearInterval(handle);
    };

    const poll = async () => {
      if (inflight || cancelled) return;
      inflight = true;
      try {
        const delta = await biApi.dashboards.changes(id, sinceRef.current);
        if (cancelled) return;
        sinceRef.current = delta.serverTime;
        failureLogged = false;
        // A layout save, a widget rename, OR a widget delete is its own raw
        // optimistic write into this exact cache entry; this poll is NOT a
        // TanStack query, so their `cancelQueries` can't reach it. If any is
        // mid-flight, skip the delta write so a poll landing between the
        // optimistic patch and the mutation's settle can't clobber the user's
        // just-made change — in particular, a stale `/changes` poll can't
        // resurrect a widget the user just deleted. The cursor still advances
        // (so the next tick after the mutation settles only carries genuinely-
        // new changes), and each mutation's `onSettled` invalidate refetches to
        // reconcile.
        if (
          qc.isMutating({ mutationKey: biKeys.dashboardSaveLayout(id) }) > 0 ||
          qc.isMutating({ mutationKey: biKeys.dashboardRenameWidget(id) }) >
            0 ||
          qc.isMutating({ mutationKey: biKeys.dashboardDeleteWidget(id) }) > 0
        ) {
          return;
        }
        qc.setQueryData<DashboardPayload>(biKeys.dashboardDetail(id), (prev) =>
          applyDashboardDelta(prev, delta),
        );
      } catch (err) {
        // A 404 is TERMINAL: the dashboard was deleted or is no longer the
        // caller's, so it will never come back on this id — stop the loop
        // entirely (retrying just 404s forever). The effect re-runs and
        // restarts the poll if `id`/`active` change.
        if ((err as { status?: number })?.status === 404) {
          stop();
          return;
        }
        // Any other failure is usually transient (offline, a 5xx blip) — keep
        // the last cursor and let the next tick retry. But a PERSISTENT failure
        // (e.g. a 500 from the changes endpoint) must not be silent: swallowing
        // it makes a hard server error indistinguishable from "no new widgets",
        // so AI-authored widgets just never appear with zero signal. Warn once
        // per failure streak in dev so it's diagnosable; reset on the next
        // success.
        if (import.meta.env.DEV && !failureLogged) {
          failureLogged = true;
          console.warn(
            `[bi] live-widget poll failed for dashboard ${id}; ` +
              `retrying every ${DASHBOARD_POLL_INTERVAL_MS}ms`,
            err,
          );
        }
      } finally {
        inflight = false;
      }
    };

    void poll(); // bootstrap the cursor immediately, don't wait a full interval
    handle = setInterval(
      poll,
      active ? DASHBOARD_POLL_INTERVAL_MS : DASHBOARD_IDLE_POLL_INTERVAL_MS,
    );
    return stop;
    // `kick` is a dependency on purpose: a bump re-runs the effect, whose
    // `void poll()` fires an immediate `/changes` read. The cursor lives in a
    // ref across restarts, so this catches the new widget without re-bootstrapping.
  }, [id, active, qc, kick]);
}

/** All widgets in the company (building blocks; company-wide). */
export function useWidgets() {
  return useQuery({
    queryKey: biKeys.widgetList(),
    queryFn: () => biApi.widgets.list(),
  });
}

/** One widget by id. */
export function useWidget(id: string | null | undefined) {
  return useQuery({
    queryKey: biKeys.widgetDetail(id ?? ""),
    queryFn: () => biApi.widgets.get(id as string),
    enabled: !!id,
  });
}

/** All saved queries in the company. (`useBiQuery*` to avoid the TanStack `useQuery` clash.) */
export function useBiQueries() {
  return useQuery({
    queryKey: biKeys.queryList(),
    queryFn: () => biApi.queries.list(),
  });
}

/** One saved query with its cached result. */
export function useBiQuery(id: string | null | undefined) {
  return useQuery({
    queryKey: biKeys.queryDetail(id ?? ""),
    queryFn: () => biApi.queries.get(id as string),
    enabled: !!id,
  });
}

// ----------------------------------------------------- dashboard writes ----

export function useCreateDashboard() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: DashboardCreateInput) => biApi.dashboards.create(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: biKeys.dashboardList() });
    },
  });
}

export function useUpdateDashboard() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: DashboardUpdateInput }) =>
      biApi.dashboards.update(id, input),
    onSuccess: (_dashboard: Dashboard, { id }) => {
      qc.invalidateQueries({ queryKey: biKeys.dashboardList() });
      qc.invalidateQueries({ queryKey: biKeys.dashboardDetail(id) });
    },
  });
}

export function useDeleteDashboard() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => biApi.dashboards.remove(id),
    onSuccess: (_dashboard: Dashboard, id) => {
      qc.removeQueries({ queryKey: biKeys.dashboardDetail(id) });
      qc.invalidateQueries({ queryKey: biKeys.dashboardList() });
    },
  });
}

/**
 * Reorder the user's tabs. Optimistic: the dragged order is written into the
 * list cache immediately (with rewritten `position`s) so the strip doesn't snap
 * back while the request is in flight; a failure rolls back to the snapshot and
 * the caller surfaces a toast. Always re-syncs from the server on settle.
 */
export function useReorderDashboards() {
  const qc = useQueryClient();
  return useMutation<Dashboard[], Error, string[], { prev?: Dashboard[] }>({
    mutationFn: (dashboardIds: string[]) =>
      biApi.dashboards.reorder(dashboardIds),
    onMutate: async (dashboardIds) => {
      await qc.cancelQueries({ queryKey: biKeys.dashboardList() });
      const prev = qc.getQueryData<Dashboard[]>(biKeys.dashboardList());
      if (prev) {
        const byId = new Map(prev.map((d) => [d.id, d]));
        // Reorder the known dashboards by the requested id sequence, stamping
        // the new dense positions; ignore unknown ids defensively.
        const next = dashboardIds
          .map((id) => byId.get(id))
          .filter((d): d is Dashboard => d !== undefined)
          .map((d, i) => ({ ...d, position: i }));
        qc.setQueryData<Dashboard[]>(biKeys.dashboardList(), next);
      }
      return { prev };
    },
    onError: (_err, _ids, ctx) => {
      if (ctx?.prev) qc.setQueryData(biKeys.dashboardList(), ctx.prev);
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: biKeys.dashboardList() });
    },
  });
}

/**
 * Make a dashboard the user's default landing tab. Optimistic: flips
 * `isDefault` in the list cache (target true, every other false) so the star
 * updates instantly; rolls back on failure and re-syncs on settle.
 */
export function useSetDefaultDashboard() {
  const qc = useQueryClient();
  return useMutation<Dashboard, Error, string, { prev?: Dashboard[] }>({
    mutationFn: (id: string) => biApi.dashboards.setDefault(id),
    onMutate: async (id) => {
      await qc.cancelQueries({ queryKey: biKeys.dashboardList() });
      const prev = qc.getQueryData<Dashboard[]>(biKeys.dashboardList());
      if (prev) {
        qc.setQueryData<Dashboard[]>(
          biKeys.dashboardList(),
          prev.map((d) => ({ ...d, isDefault: d.id === id })),
        );
      }
      return { prev };
    },
    onError: (_err, _id, ctx) => {
      if (ctx?.prev) qc.setQueryData(biKeys.dashboardList(), ctx.prev);
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: biKeys.dashboardList() });
    },
  });
}

/**
 * Flip `isSeen` on the given placements in a cached payload. Returns the same
 * reference when nothing changes, and keeps every untouched placement's
 * identity, so the grid doesn't re-render cards that didn't move.
 */
function applyWidgetsSeenToPayload(
  prev: DashboardPayload | undefined,
  placementIds: string[],
): DashboardPayload | undefined {
  if (!prev) return prev;
  const ids = new Set(placementIds);
  let changed = false;
  const widgets = prev.widgets.map((w) => {
    if (!ids.has(w.id) || w.isSeen) return w;
    changed = true;
    return { ...w, isSeen: true };
  });
  return changed ? { ...prev, widgets } : prev;
}

/**
 * Report widgets the user has actually seen on screen (STK-1658).
 *
 * NOT optimistic: seeing SOME widgets says nothing about whether the board is
 * now read — only the server knows whether an unseen one remains below the
 * fold. It patches the detail cache only AFTER the server confirms, so a poll
 * landing either side of it can't contradict the value and no `isMutating`
 * fence is needed in `useDashboardLiveWidgets`.
 */
export function useMarkDashboardWidgetsSeen() {
  const qc = useQueryClient();
  return useMutation<
    { marked: number; markedIds: string[] },
    Error,
    { dashboardId: string; placementIds: string[] }
  >({
    mutationFn: ({ dashboardId, placementIds }) =>
      biApi.dashboards.markWidgetsSeen(dashboardId, placementIds),
    onSuccess: ({ markedIds }, { dashboardId }) => {
      // Nothing changed server-side (a re-report) → no cache work at all.
      if (markedIds.length > 0) {
        // Patch with the ids the SERVER actually wrote (the still-unseen rows it
        // flipped), not the ones we sent — a concurrent session may have marked
        // some seen since our last fetch, so the sent set can be wider than what
        // this POST changed. Without this the cached payload keeps
        // `isSeen: false` until it next refetches, so leaving the board and
        // coming back would re-flash the accent on cards already read.
        qc.setQueryData<DashboardPayload>(
          biKeys.dashboardDetail(dashboardId),
          (prev) => applyWidgetsSeenToPayload(prev, markedIds),
        );
        qc.invalidateQueries({ queryKey: biKeys.dashboardList() });
      }
    },
  });
}

/**
 * Apply a saved layout to a cached dashboard payload: set each placement's
 * position/width/height from the submitted set (keyed by id), then sort by
 * position so the grid renders in the new order immediately. A placement the
 * save didn't mention keeps its current layout. Returns the SAME reference when
 * there is no cached payload so React short-circuits.
 */
function applyLayoutToPayload(
  prev: DashboardPayload | undefined,
  placements: LayoutPlacementInput[],
): DashboardPayload | undefined {
  if (!prev) return prev;
  const byId = new Map(placements.map((p) => [p.id, p]));
  const widgets = prev.widgets
    .map((w) => {
      const next = byId.get(w.id);
      return next
        ? {
            ...w,
            position: next.position,
            width: next.width,
            height: next.height,
          }
        : w;
    })
    .sort((a, b) => a.position - b.position);
  return { ...prev, widgets };
}

/**
 * Persist a whole-dashboard grid layout (the edit-mode "Save"). Optimistically
 * patches the new layout into the cached dashboard payload so the grid settles
 * instantly, then invalidates the detail query to reconcile with the server's
 * authoritative ordering. Two writers can otherwise clobber the optimistic
 * patch mid-save, so both are fenced off: `cancelQueries` aborts any in-flight
 * `useDashboard` detail refetch, and the live `/changes` poll — a raw
 * `setInterval` that `cancelQueries` can't reach — pauses its cache write while
 * this mutation is in flight by checking `isMutating` on `dashboardSaveLayout`.
 * The `mutationKey` is what the poll watches. Restores the pre-save snapshot if
 * the save fails.
 */
export function useSaveDashboardLayout(dashboardId: string | null | undefined) {
  const qc = useQueryClient();
  return useMutation<
    Awaited<ReturnType<typeof biApi.dashboards.saveLayout>>,
    Error,
    LayoutPlacementInput[],
    { previous?: DashboardPayload }
  >({
    mutationKey: biKeys.dashboardSaveLayout(dashboardId ?? ""),
    mutationFn: (placements) => {
      if (!dashboardId) {
        return Promise.reject(new Error("missing dashboard id"));
      }
      return biApi.dashboards.saveLayout(dashboardId, placements);
    },
    onMutate: async (placements) => {
      if (!dashboardId) return {};
      // Stop any in-flight `useDashboard` detail refetch so a stale server
      // response can't overwrite the optimistic layout. The live `/changes` poll
      // is fenced separately (it watches this mutation's `isMutating` state) —
      // `cancelQueries` can't stop a raw setInterval.
      await qc.cancelQueries({ queryKey: biKeys.dashboardDetail(dashboardId) });
      const previous = qc.getQueryData<DashboardPayload>(
        biKeys.dashboardDetail(dashboardId),
      );
      qc.setQueryData<DashboardPayload>(
        biKeys.dashboardDetail(dashboardId),
        (prev) => applyLayoutToPayload(prev, placements),
      );
      return { previous };
    },
    onError: (_err, _placements, context) => {
      if (!dashboardId || !context?.previous) return;
      qc.setQueryData(biKeys.dashboardDetail(dashboardId), context.previous);
    },
    onSettled: () => {
      if (!dashboardId) return;
      qc.invalidateQueries({ queryKey: biKeys.dashboardDetail(dashboardId) });
    },
  });
}

// --------------------------------------------------------- widget rename ----

/**
 * Patch a widget's title into a cached dashboard payload: rewrite the title on
 * every placement that renders the widget (a widget can be pinned more than
 * once). Each touched placement gets a NEW `widget` object identity so its card
 * re-renders with the new title; untouched placements keep their identity (no
 * chart re-render). Returns the SAME reference when nothing matched so React
 * short-circuits.
 */
function applyWidgetTitleToPayload(
  prev: DashboardPayload | undefined,
  widgetId: string,
  title: string,
): DashboardPayload | undefined {
  if (!prev) return prev;
  let touched = false;
  const widgets = prev.widgets.map((dw) => {
    if (dw.widget?.id === widgetId && dw.widget.title !== title) {
      touched = true;
      return { ...dw, widget: { ...dw.widget, title } };
    }
    return dw;
  });
  return touched ? { ...prev, widgets } : prev;
}

/**
 * Rename a widget's title (STK-734) — the one user-facing widget mutation.
 * Optimistic against the dashboard detail cache: the new title shows instantly
 * on every card that renders the widget, rolling back to the snapshot if the
 * PATCH fails, and reconciling with the server on settle.
 *
 * Two writers can otherwise clobber the optimistic patch mid-rename, so both
 * are fenced exactly like {@link useSaveDashboardLayout}: `cancelQueries` aborts
 * any in-flight `useDashboard` detail refetch, and the live `/changes` poll — a
 * raw `setInterval` `cancelQueries` can't reach — pauses its cache write while
 * this mutation is in flight by checking `isMutating` on the
 * `dashboardRenameWidget` key (the `mutationKey` set below). Bound to a
 * dashboard id so the optimistic write and the poll fence target this board.
 */
export function useRenameWidget(dashboardId: string | null | undefined) {
  const qc = useQueryClient();
  return useMutation<
    Widget,
    Error,
    { widgetId: string; title: string },
    { previous?: DashboardPayload }
  >({
    mutationKey: biKeys.dashboardRenameWidget(dashboardId ?? ""),
    mutationFn: ({ widgetId, title }) => biApi.widgets.rename(widgetId, title),
    onMutate: async ({ widgetId, title }) => {
      if (!dashboardId) return {};
      // Stop any in-flight `useDashboard` detail refetch so a stale server
      // response can't overwrite the optimistic title. The live `/changes` poll
      // is fenced separately (it watches this mutation's `isMutating` state).
      await qc.cancelQueries({ queryKey: biKeys.dashboardDetail(dashboardId) });
      const previous = qc.getQueryData<DashboardPayload>(
        biKeys.dashboardDetail(dashboardId),
      );
      qc.setQueryData<DashboardPayload>(
        biKeys.dashboardDetail(dashboardId),
        (prev) => applyWidgetTitleToPayload(prev, widgetId, title),
      );
      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (!dashboardId || !context?.previous) return;
      qc.setQueryData(biKeys.dashboardDetail(dashboardId), context.previous);
    },
    onSettled: (_widget, _err, { widgetId }) => {
      if (dashboardId) {
        qc.invalidateQueries({ queryKey: biKeys.dashboardDetail(dashboardId) });
      }
      // The standalone widget read (if any consumer holds it) is now stale.
      qc.invalidateQueries({ queryKey: biKeys.widgetDetail(widgetId) });
    },
  });
}

// --------------------------------------------------------- widget delete ----

/**
 * Drop a widget from a cached dashboard payload: remove EVERY placement that
 * renders the widget (a widget can be pinned more than once). Returns the SAME
 * reference when nothing matched so React short-circuits. Positions aren't
 * renumbered here — the `onSettled` invalidate refetches the authoritative
 * (gap-closed) layout; this is just an instant visual removal.
 */
function removeWidgetFromPayload(
  prev: DashboardPayload | undefined,
  widgetId: string,
): DashboardPayload | undefined {
  if (!prev) return prev;
  const widgets = prev.widgets.filter((dw) => dw.widget?.id !== widgetId);
  return widgets.length !== prev.widgets.length ? { ...prev, widgets } : prev;
}

/**
 * Delete a widget — the other user-facing widget mutation alongside the rename.
 * Optimistic against the dashboard detail cache: the card disappears instantly
 * from every placement that renders the widget, rolling back to the snapshot if
 * the DELETE fails, and reconciling with the server (the gap-closed layout) on
 * settle.
 *
 * Fenced exactly like {@link useRenameWidget} / {@link useSaveDashboardLayout}:
 * `cancelQueries` aborts any in-flight `useDashboard` detail refetch, and the
 * live `/changes` poll — a raw `setInterval` `cancelQueries` can't reach —
 * pauses its cache write while this mutation is in flight by checking
 * `isMutating` on the `dashboardDeleteWidget` key (the `mutationKey` set below),
 * so a stale poll can't resurrect the just-deleted widget. Bound to a dashboard
 * id so the optimistic write and the poll fence target this board.
 */
export function useDeleteWidget(dashboardId: string | null | undefined) {
  const qc = useQueryClient();
  return useMutation<
    void,
    Error,
    { widgetId: string },
    { previous?: DashboardPayload }
  >({
    mutationKey: biKeys.dashboardDeleteWidget(dashboardId ?? ""),
    mutationFn: ({ widgetId }) => biApi.widgets.deleteWidget(widgetId),
    onMutate: async ({ widgetId }) => {
      if (!dashboardId) return {};
      // Stop any in-flight `useDashboard` detail refetch so a stale server
      // response can't re-add the optimistically-removed widget. The live
      // `/changes` poll is fenced separately (it watches this mutation's
      // `isMutating` state).
      await qc.cancelQueries({ queryKey: biKeys.dashboardDetail(dashboardId) });
      const previous = qc.getQueryData<DashboardPayload>(
        biKeys.dashboardDetail(dashboardId),
      );
      qc.setQueryData<DashboardPayload>(
        biKeys.dashboardDetail(dashboardId),
        (prev) => removeWidgetFromPayload(prev, widgetId),
      );
      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (!dashboardId || !context?.previous) return;
      qc.setQueryData(biKeys.dashboardDetail(dashboardId), context.previous);
    },
    onSettled: (_data, _err, { widgetId }) => {
      if (dashboardId) {
        qc.invalidateQueries({ queryKey: biKeys.dashboardDetail(dashboardId) });
      }
      // The standalone widget read (if any consumer holds it) is now stale.
      qc.invalidateQueries({ queryKey: biKeys.widgetDetail(widgetId) });
    },
  });
}

// -------------------------------------------------------- widget refresh ----

/**
 * Patch a freshly-computed widget result into every cached dashboard payload
 * that places it (marking it no longer stale) and drop the now-stale cached
 * reads for the widget + its backing query. The backend returns the new result
 * inline, so this needs no refetch — the merge mirrors exactly what a
 * subsequent resolver read would produce (`executeSaved` stores the same
 * payload as `query_results.value` with `computed_at = computedAt`).
 *
 * Shared by the single-widget {@link useRefreshWidget} and the dashboard-wide
 * {@link useRefreshDashboardWidgets} so both update the board identically.
 */
function patchRefreshedWidget(
  qc: QueryClient,
  widgetId: string,
  payload: QueryExecutionPayload,
): void {
  let queryId: string | null = null;
  qc.setQueriesData<DashboardPayload>(
    { queryKey: biKeys.dashboardDetails() },
    (prev) => {
      if (!prev) return prev;
      let touched = false;
      const widgets = prev.widgets.map((dw) => {
        if (dw.widget?.id === widgetId) {
          touched = true;
          queryId = dw.widget.queryId;
          const widget: ResolvedWidget = {
            ...dw.widget,
            result: { value: payload, computedAt: payload.computedAt },
            isStale: false,
          };
          return { ...dw, widget };
        }
        return dw;
      });
      return touched ? { ...prev, widgets } : prev;
    },
  );
  qc.invalidateQueries({ queryKey: biKeys.widgetDetail(widgetId) });
  // The widget's saved query result changed too — drop its cached read so
  // `useBiQuery(queryId)` refetches rather than serving the stale result.
  // Prefer the queryId seen in the dashboard merge; otherwise fall back to the
  // standalone widget cache so we still invalidate when no dashboard currently
  // holds the widget.
  queryId ??=
    qc.getQueryData<Widget>(biKeys.widgetDetail(widgetId))?.queryId ?? null;
  if (queryId) {
    qc.invalidateQueries({ queryKey: biKeys.queryDetail(queryId) });
  }
}

/**
 * Re-run a single widget's saved query and patch the fresh result into the
 * cache. Errors surface as {@link WidgetRefreshError} (e.g. `execution_busy`
 * → 429).
 */
export function useRefreshWidget() {
  const qc = useQueryClient();
  return useMutation<QueryExecutionPayload, WidgetRefreshError, string>({
    mutationFn: (widgetId: string) => biApi.widgets.refresh(widgetId),
    onSuccess: (payload, widgetId) =>
      patchRefreshedWidget(qc, widgetId, payload),
  });
}

/**
 * Client-side cap on concurrent refreshes for a "refresh all". Kept under the
 * server executor's own cap (`BI_QUERY_MAX_CONCURRENT`, default 6) so a single
 * dashboard's bulk refresh can't saturate the shared connection pool and 429
 * itself; the rest of the slots stay available for other callers.
 */
const REFRESH_ALL_CONCURRENCY = 3;

/** Summary of a dashboard-wide refresh — lets the caller surface ONE toast
 *  rather than one per failed widget (each success already patched its card). */
export interface RefreshAllOutcome {
  total: number;
  failed: number;
  /** At least one failure was a 429 (executor at capacity) — retryable. */
  busy: boolean;
}

/**
 * Refresh every widget on a dashboard at once (the toolbar "Refresh all"
 * action). Fans the per-widget refresh out with bounded concurrency, patching
 * each fresh result into the dashboard cache the moment it lands — so cards
 * update progressively and the burst stays under the executor's cap. The
 * mutation never rejects: per-widget failures are tallied into a
 * {@link RefreshAllOutcome} so a partial failure still commits every success.
 */
export function useRefreshDashboardWidgets() {
  const qc = useQueryClient();
  return useMutation<RefreshAllOutcome, never, string[]>({
    mutationFn: async (widgetIds) => {
      let failed = 0;
      let busy = false;
      const queue = [...widgetIds];
      const worker = async () => {
        for (let id = queue.shift(); id !== undefined; id = queue.shift()) {
          try {
            patchRefreshedWidget(qc, id, await biApi.widgets.refresh(id));
          } catch (err) {
            failed += 1;
            if (
              err instanceof WidgetRefreshError &&
              err.code === "execution_busy"
            ) {
              busy = true;
            }
          }
        }
      };
      await Promise.all(
        Array.from(
          { length: Math.min(REFRESH_ALL_CONCURRENCY, widgetIds.length) },
          worker,
        ),
      );
      return { total: widgetIds.length, failed, busy };
    },
  });
}
